<script setup lang="ts">
import type { ISharePayload } from "~/videos/components/Event/ModalShare.vue";
import { formatTime } from "@/videos/helpers/format-time";

const { t } = useI18n();
const toast = useToast();
const appEnv = useRuntimeConfig().public.appEnv;

const isDev = computed(() => appEnv === "development" || appEnv === "local");

const props = withDefaults(
  defineProps<{
    url: string;
    time?: number;
    text?: string;
    hasEmailProvider?: boolean;
    hasProviders?: boolean;
    allowIncludeStats?: boolean;
    allowExpirationDate?: boolean;
    allowSaving?: boolean;
    allowShareAngles?: boolean;
    anglesOptions?: { label: string; value: string }[];
  }>(),
  {
    hasProviders: true,
    allowIncludeStats: false,
    allowExpirationDate: false,
    allowSaving: false,
    allowShareAngles: false,
    anglesOptions: () => [],
  }
);

const emit = defineEmits<{
  (event: "share", provider: string): void;
  (event: "change", payload: ISharePayload): void;
}>();

const isTimeShared = ref(false);
const includeStats = ref(true);
const allowSavingShared = ref(false);
const isShareAnglesSelected = ref(false);
const sharedAngles = ref([]);
const expirationDate = ref<"no-expiration" | "1-week" | "2-weeks" | "10-minutes" | "20-minutes">("no-expiration");

const shareUrl = computed(() => {
  if (!props.url) {
    // Returning empty string in case share url isn't yet ready
    return "";
  }

  if (isTimeShared.value && props.time) return `${props.url}?time=${props.time}`;
  return props.url;
});
const shareData = computed(() => ({ url: shareUrl.value, title: props.text || "" }));
const shareProviders = computed(() => {
  if (props.hasEmailProvider) return SHARE_PROVIDERS.concat(SHARE_PROVIDER.EMAIL);
  return SHARE_PROVIDERS;
});

const { shareProviderUrl } = useSharing(shareData);
const { copy, isSupported: isCopySupported } = useClipboard({ source: shareUrl.value });

// determine if the URL has been shortened by Branch.io or not. Non-short URLs can't be shared on Facebook
const isShortUrl = computed(() => {
  if (!shareUrl.value) {
    return false;
  }

  const shareUrlObj = new URL(shareUrl.value);
  const params = new URLSearchParams(shareUrlObj.search);

  return params.has("accessToken");
});

function onClipboardCopyClick() {
  copy(shareUrl.value).then(() => toast.success(t("labels.url_copied_success_message")));
  onShare("clipboard copy");
}

function onShare(provider: string) {
  emit("share", provider);
}

function getExpirationTime(expirationDate: string): number | null {
  let expiresAt: number | null = null;

  switch (expirationDate) {
    case "10-minutes":
      expiresAt = 10 * 60;
      break;
    case "20-minutes":
      expiresAt = 20 * 60;
      break;
    case "1-week":
      expiresAt = 7 * 24 * 60 * 60;
      break;
    case "2-weeks":
      expiresAt = 14 * 24 * 60 * 60;
      break;
    default:
      expiresAt = null;
  }

  return expiresAt;
}

watch(
  () => [includeStats.value, expirationDate.value, allowSavingShared.value, sharedAngles.value],
  () => {
    const expirationTime = getExpirationTime(expirationDate.value);
    emit("change", { includeStats: includeStats.value, expirationTime, allowSaving: allowSavingShared.value, sharedAngles: sharedAngles.value });
  }
);

watch(isShareAnglesSelected, (bool) => {
  if (!bool) {
    sharedAngles.value = [];
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div v-if="props.hasProviders" class="flex justify-center gap-8">
      <template v-for="provider in shareProviders" :key="provider">
        <a
          v-if="provider !== 'Facebook' || !isShortUrl"
          v-track="'share'"
          :href="shareProviderUrl(provider)"
          target="_blank"
          aria-label="Share"
          @click="onShare(provider)"
        >
          <RIcon :name="`share/${provider.toLowerCase()}`" class="inline-block text-[56px]" filled />
          <p class="mt-2 text-center text-xs font-medium text-neutral-light-800">
            {{ provider }}
          </p>
        </a>
      </template>
    </div>

    <div class="flex flex-col gap-6">
      <UInput
        :model-value="shareUrl"
        :disabled="!shareUrl"
        :trailing-icon="{ name: 'copy', disabled: !isCopySupported, onClick: onClipboardCopyClick }"
        readonly
      />
      <div v-if="props.time && shareUrl" class="flex items-center gap-2 dark:text-white">
        <UCheckbox
          v-model="isTimeShared"
          name="share-with-time"
          :label="t('labels.share_time', { time: formatTime(props.time) })"
        />
      </div>

      <!-- Include stats checkbox -->
      <UCheckbox
        v-if="props.allowIncludeStats"
        v-model="includeStats"
        name="include-stats"
        :label="t('labels.include_stats')"
      />

      <!-- Allow saving checkbox -->
      <div v-if="props.allowSaving" class="flex items-center gap-2">
        <UCheckbox v-model="allowSavingShared" name="allow-saving" :label="t('labels.allow_saving')" />
        <UIcon v-tooltip="t('modals.events.tooltips.allow_saving')" name="info" class="text-neutral-light-800" />
      </div>

      <!-- Allow share specific angles checkbox -->
      <div v-if="props.allowShareAngles" class="flex items-center gap-2">
        <UCheckbox v-model="isShareAnglesSelected" name="share-specific-angles" :label="t('labels.share_specific_angles')" />
      </div>

      <!-- Select menu to choose one or more angles to share -->
      <UFormGroup v-if="isShareAnglesSelected" :label="t('labels.select_one_or_more_angles', { type: t('labels.share') })" name="shared-angles">
        <USelectMenu
          v-model="sharedAngles"
          :options="props.anglesOptions"
          :placeholder="t('errors.no_selected_angle')"
          nullable
          multiple
          select-all-as-item-list
          show-selected-options-labels
        />
      </UFormGroup>

      <!-- Expiration date radio group -->
      <div v-if="props.allowExpirationDate">
        <div class="mb-4 text-xs font-semibold text-neutral-light-800">
          {{ t('labels.expiration_date') }}
        </div>
        <div class="flex flex-wrap gap-4">
          <URadio
            v-model="expirationDate"
            name="expiration-date"
            :value="'no-expiration'"
            :label="t('labels.without_expiration')"
          />
          <URadio
            v-if="isDev"
            v-model="expirationDate"
            name="expiration-date"
            :value="'10-minutes'"
            label="10 minutes"
          />
          <URadio
            v-if="isDev"
            v-model="expirationDate"
            name="expiration-date"
            :value="'20-minutes'"
            label="20 minutes"
          />
          <URadio
            v-model="expirationDate"
            name="expiration-date"
            :value="'1-week'"
            label="1 week"
          />
          <URadio
            v-model="expirationDate"
            name="expiration-date"
            :value="'2-weeks'"
            label="2 weeks"
          />
        </div>
      </div>
    </div>
  </div>
</template>
